import { Box, Grid } from '@mui/material';
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import sortBy from 'lodash/sortBy';
import { useMemo } from 'react';

import { SingleOperators } from 'common/src/query-params/operators';
import { DataInsightPriority } from 'common/src/types/enums/DataInsightPriority.enum';
import { DataInsightType } from 'common/src/types/enums/DataInsightType.enum';

import { IRelatedDataInsightFilterValues } from './DataInsights';

import DashboardAutoCompleteFilterCard from '../dashboard/DashboardAutoCompleteFilterCard';
import DashboardFilterCard from '../dashboard/DashboardFilterCard';

interface IDataInsightsFilters {
  disabled: boolean;
  filterModel: any;
  setFilterModel: (e) => void;
  relatedFilterValues: IRelatedDataInsightFilterValues;
}

const DataInsightsFilters = ({
  disabled,
  filterModel,
  setFilterModel,
  relatedFilterValues,
}: IDataInsightsFilters) => {
  const { utilities, accounts, meters, facilities } = relatedFilterValues;
  const dateItem = filterModel.items.find(({ field }) => field === 'date');

  const handleDateChange = (value: [Date, Date]) => {
    setFilterModel((prev) => {
      const items = prev.items.map((item) =>
        item.field === 'date' ? { ...item, value } : item,
      );

      if (!dateItem) {
        items.push({
          id: Math.random(),
          field: 'date',
          operator: SingleOperators.BETWEEN,
          value,
        });
      }

      return { items };
    });
  };

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Type"
            autocompleteProps={{
              disabled,
              filters: useMemo(
                () => ({
                  field: 'type',
                  operator: 'isAnyOf',
                }),
                [],
              ),
              setFilter: setFilterModel,
              options: sortBy(
                Object.values(DataInsightType).map((item) => ({
                  label: item,
                  value: item,
                })),
                'label',
              ),
            }}
          />
        </Grid>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Priority"
            autocompleteProps={{
              disabled,
              filters: useMemo(
                () => ({
                  field: 'priority',
                  operator: 'isAnyOf',
                }),
                [],
              ),
              setFilter: setFilterModel,
              options: Object.values(DataInsightPriority).map((item) => ({
                label: item,
                value: item,
              })),
            }}
          />
        </Grid>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Utility"
            autocompleteProps={{
              disabled,
              filters: useMemo(
                () => ({
                  field: 'utilityName',
                  operator: 'isAnyOf',
                }),
                [],
              ),
              setFilter: setFilterModel,
              options: utilities.map((item) => ({
                value: item,
                label: item,
              })),
            }}
          />
        </Grid>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Account"
            autocompleteProps={{
              disabled,
              filters: useMemo(
                () => ({
                  field: 'accountNumber',
                  operator: 'isAnyOf',
                }),
                [],
              ),
              setFilter: setFilterModel,
              options: accounts.map((item) => ({
                value: item,
                label: item,
              })),
            }}
          />
        </Grid>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Meter"
            autocompleteProps={{
              disabled,
              filters: useMemo(
                () => ({
                  field: 'meterName',
                  operator: 'isAnyOf',
                }),
                [],
              ),
              setFilter: setFilterModel,
              options: meters.map((item) => ({
                value: item,
                label: item,
              })),
            }}
          />
        </Grid>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Facility"
            autocompleteProps={{
              disabled,
              filters: useMemo(
                () => ({
                  field: 'facilityName',
                  operator: 'isAnyOf',
                }),
                [],
              ),
              setFilter: setFilterModel,
              options: facilities.map((item) => ({
                value: item,
                label: item,
              })),
            }}
          />
        </Grid>
        <Grid size={6}>
          <DashboardFilterCard title="Filter on Date">
            <DateRangePicker
              value={dateItem?.value ?? [null, null]}
              onChange={handleDateChange}
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{
                field: { clearable: true },
                textField: {
                  size: 'small',
                  sx: {
                    width: '100%',
                  },
                },
              }}
            />
          </DashboardFilterCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataInsightsFilters;
